import * as React from "react";
import { useEffect, useState } from "react";
import { Attribute, CollectionDataCache } from "@/types/collection";
import { useWallet } from "@/context/WalletContext";
import { useCollectionStats } from "@/hooks/useCollectionStats";
import { useWhitelistStatus } from "@/hooks/useWhitelistStatus";
import { useOwnerWhitelist } from "@/hooks/useOwnerWhitelist";
import LoadingStatus from "@/components/atomics/loading-status";
import { loadingData } from "@/data/loading-data";
import HeaderSection from "@/components/molecules/mint/header-section";
import NFTSection from "@/components/molecules/mint/nft-section";
import InfoSection from "@/components/molecules/mint/info-section";
import MintSection from "@/components/molecules/mint/mint-section";
import StatsSection from "@/components/molecules/mint/stats-section";
import ModalSection from "@/components/molecules/mint/modal-section";
import WhitelistSection from "@/components/molecules/mint/whitelist-section";
import WithdrawManager from "@/components/molecules/white-list/withdraw-manager";
import { ItemDataType } from "@/types";
import { backend } from "@/services/backend";
import { useStarPayment } from "@/hooks/useStarPayment";

interface MintProps {
  collection: string;
  collectionInfo: CollectionDataCache;
}

export default function Mint({ collection, collectionInfo }: MintProps) {
  const { walletAddress } = useWallet();
  const [isIOS, setIsIOS] = useState<boolean>(false);
  const [itemData, setItemData] = useState<ItemDataType>();
  const [attributes, setAttributes] = useState<Attribute[]>([]);
  const [currentStars, setCurrentStars] = useState<number>(
    collectionInfo?.stars || 0
  );

  const { sendStars } = useStarPayment({
    onSuccess: async () => {
      const updatedCollectionData = await backend.getCollectionData(collection);
      if (updatedCollectionData?.stars !== undefined) {
        setCurrentStars(updatedCollectionData.stars);
      }
    },
  });

  const {
    stats,
    royaltyPercent,
    nftCards,
    items,
    loading,
    previewImage,
    isMinting,
    isLoadingItems,
    isMintingAllowed,
    mintNft,
    handleModalClose,
    handleLoadMore,
    handleViewToggle,
    state,
    showWalletConnectionModal,
    showSuccessMessage,
    showErrorMessage,
    previousExperience,
  } = useCollectionStats(collection, collectionInfo);

  const whitelistStatus = useWhitelistStatus({
    enabled: stats.enable_whitelist,
    wallet: walletAddress,
    collection: collection,
  });

  const ownerWhitelist = useOwnerWhitelist({
    collection: collection,
  });

  useEffect(() => {
    const userAgent =
      navigator.userAgent || navigator.vendor || (window as any).opera;
    setIsIOS(/iPad|iPhone|iPod/.test(userAgent));

    // Fetch attributes
    backend
      .getCollectionItemAttributes(collection)
      .then(res => {
        setAttributes(res);
      })
      .catch(console.error);
  }, []);

  useEffect(() => {
    if (showSuccessMessage) {
      backend.getItemData(stats.nextItemAddress).then(res => {
        if (res) {
          setItemData(res);
        }
      });
    }
  }, [showSuccessMessage]);

  useEffect(() => {
    if (collectionInfo?.stars !== undefined) {
      setCurrentStars(collectionInfo.stars);
    }
  }, [collectionInfo?.stars]);

  const handleStarVote = async (starCount: number) => {
    await sendStars(collection, starCount);
  };

  // if (!loading.loaded)
  //   return (
  //     <div className="flex flex-col h-screen">
  //       <LoadingStatus status="loading" text={["Loading..."]} />
  //     </div>
  //   );

  const isLoading =
    isMinting || whitelistStatus.isLoadingProfile || ownerWhitelist.isLoading;

  return (
    <>
      {isLoading && (
        <LoadingStatus status="loading" text={loadingData[1].text} />
      )}
      <div
        className={`${
          isLoading
            ? "opacity-10 pointer-events-none"
            : "pointer-events-auto w-full"
        }`}
      >
        <HeaderSection
          walletAddress={walletAddress || ""}
          stats={stats}
          loading={loading}
          isOwnerView={state.isOwnerView}
          handleViewToggle={handleViewToggle}
          royaltyPercent={royaltyPercent || 0}
          collection={collection || ""}
          stars={currentStars}
          onStarVote={handleStarVote}
        />
        <MintSection
          isOwnerView={state.isOwnerView}
          loading={loading}
          stats={stats}
          walletAddress={walletAddress}
          collection={collection}
          mintNft={mintNft}
          isMinting={isMinting}
          isMintingAllowed={isMintingAllowed}
          whitelistStatus={whitelistStatus}
        />

        <StatsSection stats={stats} />

        <InfoSection
          stats={stats}
          collection={collection}
          attributes={attributes}
        />

        {state.isOwnerView && stats.enable_whitelist && (
          <WhitelistSection
            collection={collection}
            ownerWhitelist={ownerWhitelist}
          />
        )}

        {state.isOwnerView && (
          <WithdrawManager
            collection={collection}
            walletAddress={walletAddress!}
          />
        )}

        <NFTSection
          nftCards={nftCards}
          isLoadingItems={isLoadingItems}
          handleLoadMore={handleLoadMore}
          stats={stats}
          items={items}
          previewImage={previewImage}
        />

        <ModalSection
          stats={stats}
          showWalletConnectionModal={showWalletConnectionModal}
          showSuccessMessage={showSuccessMessage && !!itemData}
          showErrorMessage={showErrorMessage}
          walletAddress={walletAddress || undefined}
          itemData={itemData}
          previousExperience={previousExperience}
          handleModalClose={handleModalClose}
        />
      </div>
    </>
  );
}
